import { Attrs, EventListeners, Minze, MinzeElement } from 'minze'
import Swiper, { Navigation, Pagination, Scrollbar } from 'swiper'
import styles from 'swiper/swiper-bundle.css?inline'
import { config, Font } from '../../constants'
import { Locale } from '../../modules/locale'
import { getCardPlaceholder, isMobile } from '../../modules/utils'

// Initialize Swiper with required modules
Swiper.use([Navigation, Pagination, Scrollbar])

// Register localization keys
Locale.getInstance().registerKeySet('fr', 'artistEvents', {
  noEventsTitle: 'Aucun spectacle à venir.',
  eventsTitle: 'Événements en cours',
})

Locale.getInstance().registerKeySet('en', 'artistEvents', {
  noEventsTitle: 'No upcoming shows.',
  eventsTitle: 'Current Events',
})

// Type definitions
export interface EventItem {
  id: number
  schema: 'eventOffers' | 'exhibitionEvents' | 'eventSeries'
  status?: string
  statusFlag?: boolean
  statusFlagText?: Record<string, string>
  medias?: Array<{ media: string }>
  nameFr?: string
  nameEn?: string
  alt?: Record<string, string>
}

interface EventOffer extends EventItem {
  schema: 'eventOffers'
  eventId: number
}

interface AllEventData {
  agg_offers: EventOffer[]
  agg_event_series: EventItem[]
  agg_exhibition_events: EventItem[]
}

export interface LvcArtistEvents {
  loading: boolean
  allEvents: AllEventData
  uniqueEvents: EventItem[]
  noEvents: boolean
  swiper?: Swiper
}

export class LvcArtistEvents extends MinzeElement {
  // Component properties
  attrs: Attrs = ['loading']
  static observedAttributes = ['loading']

  allEvents: AllEventData = {
    agg_offers: [],
    agg_event_series: [],
    agg_exhibition_events: [],
  }
  uniqueEvents: EventItem[] = []
  swiper?: Swiper

  hasEvents = () => {
    return this.uniqueEvents.length > 0
  }

  handleEventData = (event: Event) => {
    // This part is to load the main event slider tiles
    const detail = (event as CustomEvent).detail
    this.allEvents = detail.events as AllEventData
    if (!this.allEvents) return
    const uniqueEventsFromOffers = this.allEvents.agg_offers.reduce((acc: any[], current: any) => {
      if (!acc.some(event => event.eventId === current.eventId)) {
        acc.push(current)
      }
      return acc
    }, [])
    this.uniqueEvents = [
      ...uniqueEventsFromOffers,
      ...this.allEvents.agg_event_series,
      ...this.allEvents.agg_exhibition_events,
    ]

    if (this?.uniqueEvents?.length) {
      const firstEvent = this.uniqueEvents?.[0]
      const eventId =
        'eventId' in firstEvent ? (firstEvent.eventId as number) : (firstEvent.id as number)
      this.selectAndEmitEventOffers(eventId, firstEvent?.schema || 'events')
    }
  }

  selectAndEmitEventOffers(id: number, schema: string) {
    let key: 'agg_offers' | 'agg_exhibition_events' | 'agg_event_series' = 'agg_offers'
    switch (schema) {
      case 'exhibitionEvents':
        key = 'agg_exhibition_events'
        break
      case 'eventSeries':
        key = 'agg_event_series'
        break
      default:
        key = 'agg_offers'
    }
    let offers = []
    if (key == 'agg_offers') {
      offers = this.allEvents?.[key].filter(event => event.eventId == id && event.schema == schema)
    } else {
      offers = this.allEvents?.[key].filter(event => event.id == id && event.schema == schema)
    }
    Minze.cast('lvc:artist-spectacles-event-offers-loaded', { offers: offers })
  }

  handleEventClick = (event: Event | any) => {
    const eventCard = (event.target as Element)?.closest('.swiper-slide') as Element | null
    if (!eventCard) return
    const schema = eventCard.getAttribute('data-schema') || 'eventOffers'
    const classList = eventCard.classList
    const classWithIndex = Array.from(classList).find((cl: string) =>
      /^swiper-slide-id-(\d+)$/.test(cl)
    )
    if (!classWithIndex) return
    const id = parseInt(classWithIndex.match(/^swiper-slide-id-(\d+)$/)?.[1] ?? '', 10)
    this.selectAndEmitEventOffers(id, schema)
  }

  onRender() {
    this.initializeSwiper()
  }

  initializeSwiper() {
    const swiperContainer = this.select('.artist_shows_swiper') as HTMLElement
    const nextButton = this.select('.artist_shows_swiper-next-wrapper') as HTMLElement
    const prevButton = this.select('.artist_shows_swiper-prev-wrapper') as HTMLElement

    if (swiperContainer && nextButton && prevButton) {
      this.swiper = new Swiper(swiperContainer, {
        speed: 400,
        direction: 'horizontal',
        spaceBetween: isMobile() ? 16 : 24,
        centeredSlides: false,
        slidesPerView: 'auto',
        slideToClickedSlide: true,
        updateOnWindowResize: true,
        on: {
          snapGridLengthChange: function () {
            // @ts-ignore
            if (this.snapGrid.length != this.slidesGrid.length) {
              // @ts-ignore
              this.snapGrid = this.slidesGrid.slice(0)
            }
          },
          activeIndexChange: (swiper: Swiper) => {
            let container = this.select('#events-container') as HTMLElement
            let el = container.children[swiper.activeIndex] as HTMLElement
            this.handleEventClick({ target: el } as unknown as Event)
          },
        },
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
      })
    }
  }

  renderEventSlider = (language: string) => {
    return /*html*/ `
      <div class="artist_shows_slider-component">
        <!-- Navigation Controls -->
        <div class="artist_shows_swiper-prev-wrapper">
          <div class="artist_shows_swiper-prev">
            <lvc-slider-arrow-left-icon></lvc-slider-arrow-left-icon>
          </div>
        </div>
        <div class="artist_shows_swiper-next-wrapper">
          <div class="artist_shows_swiper-next">
            <lvc-slider-arrow-right-icon></lvc-slider-arrow-right-icon>
          </div>
        </div>

        <!-- Swiper Container -->
        <div class="artist_shows_swiper">
          <div id="events-container" class="swiper-wrapper isartist">
            ${this.renderEventCards(language)}
          </div>
        </div>
      </div>
    `
  }

  renderEventCards = (language: string) => {
    return (
      this.uniqueEvents
        // .sort(this.compareDates)
        .map(event => this.renderEventCard(event, language))
        .join('')
    )
  }

  renderEventCard = (event: any, language: string) => {
    const isDisabled = event.status === 'Postpone' || event.status === 'Cancelled'
    const hasStatusFlag = event.statusFlag && event.statusFlagText?.[language]

    let imageUrl = ''
    try {
      const parsedImage = JSON.parse(event.medias[0].media)
      imageUrl = parsedImage?.[0]?.url || ''
    } catch (err) {
      console.error('Error parsing media')
    }
    return /*html*/ `
      <div
        data-schema="${event.schema || 'eventOffers'}"
        class="event-card swiper-slide isartist swiper-slide-id-${event.eventId || event.id} ${isDisabled ? 'disabled' : ''}"
      >
        <div class="artist_shows_slide-content">
          ${hasStatusFlag ? this.renderStatusBadge(event, language) : ''}
          
          <lvc-image
            src="${imageUrl}"
            alt="${event.alt?.[language]}"
            data-placeholder-image="${getCardPlaceholder(event.schema)}"
          ></lvc-image>
          
          <div class="event_similar-event-info-content">
            <h4 class="artist_shows_slide-title text-style-2lines">
              ${Locale.language === 'fr' ? event.nameFr : event?.nameEn || event?.nameFr}
            </h4>
          </div>
        </div>
      </div>
    `
  }

  renderStatusBadge = (event: any, language: string) => {
    return /*html*/ `
      <div class="event-status-badge">
        <p>${event.statusFlagText[language]}</p>
      </div>
    `
  }

  html = () => {
    const language = Locale.language
    const titleKey = !this.hasEvents() ? 'artistEvents.noEventsTitle' : 'artistEvents.eventsTitle'
    if (this.loading) {
      return /*html*/ `
        <div class="loader-container">
          <div class="spinner-loader"></div>
        </div>
      `
    }
    if (!this.loading && !this.allEvents) return ''
    return /*html*/ `
      <h2 class="events-title">
        ${Locale.getKey(titleKey)}
      </h2>
      ${this.hasEvents() ? this.renderEventSlider(language) : ''}
    `
  }

  css = () => `
    ${styles}
    
    /* Variables */
    :host {
      --primary-color: #1E007D;
      --white-color: #FFFFFF;
      --transition-standard: all 0.2s ease;
      --transition-medium: all 0.5s ease;
      --nav-button-size: 56px;
      --card-max-width: 320px;
      --card-active-max-width: 480px;
      --scrollbar-width: 418px;
      --scrollbar-height: 5px;
    }
    
    /* Loader */
    .spinner-loader {
      border: 4px solid rgba(30, 0, 125, 0.3);
      border-top: 4px solid #1E007D;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    /* Typography */
    .events-title {
      margin: 1.5rem 0;
      font-weight: 800;
      font-size: 40px;
      line-height: 120%;
      color: var(--primary-color);
    }
    
    .artist_shows_slide-title {
      margin-top: 0.5rem;
      margin-bottom: 0;
      max-width: var(--card-max-width);
      font-size: 1.5rem;
      font-weight: 800;
      line-height: 1.1;
      color: var(--primary-color);
      text-transform: uppercase;
      transition: margin 500ms ease, font-size 500ms ease;
    }
    
    .text-style-2lines {
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
    
    /* Layout */
    .artist_shows_slider-component {
      position: relative;
    }
    
    .swiper-wrapper.isartist {
      display: flex;
      height: 400px;
    }
    
    .artist_shows_swiper {
      z-index: 0 !important;
    }
    
    /* Navigation */
    .artist_shows_swiper-prev-wrapper,
    .artist_shows_swiper-next-wrapper {
      position: absolute;
      top: 0;
      bottom: 0;
      z-index: 2;
      display: flex;
      margin-bottom: 65px;
      flex-direction: column;
      justify-content: center;
    }
    
    .artist_shows_swiper-prev-wrapper {
      left: -15%;
      right: auto;
    }
    
    .artist_shows_swiper-next-wrapper {
      left: auto;
      right: -15%;
    }
    
    .artist_shows_swiper-prev,
    .artist_shows_swiper-next {
      width: var(--nav-button-size);
      height: var(--nav-button-size);
      border-radius: 50%;
      background-color: var(--white-color);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .swiper-button-disabled {
      display: none;
    }
    
    /* Scrollbar */
    .swiper-scrollbar-horizontal {
      width: var(--scrollbar-width) !important;
      height: var(--scrollbar-height) !important;
      margin: auto !important;
      position: static !important;
      background-color: var(--white-color) !important;
      bottom: 0 !important;
    }
    
    .swiper-scrollbar-drag {
      background-color: var(--primary-color) !important;
      height: var(--scrollbar-height) !important;
    }
    
    /* Cards */
    .event-card {
      transition: var(--transition-standard);
    }
    
    .event-card:hover {
      transform: scale(1.05);
    }
    
    lvc-image {
      pointer-events: none;
    }
    
    .swiper-slide {
      transition: var(--transition-medium) !important;
      cursor: pointer;
    }
    
    .swiper-slide.isartist {
      opacity: 0.5;
      max-width: var(--card-max-width) !important;
      transition: var(--transition-standard) !important;
    }
    
    .swiper-slide.isartist.swiper-slide-active {
      max-width: var(--card-active-max-width) !important;
      opacity: 1;
    }
    
    /* Card Content */
    .event_similar-event-info-content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
    
    /* Active Slide Styles */
    .swiper-slide.isartist.swiper-slide-active > .artist_shows_slide-content > .event_similar-event-info-content > .artist_shows_slide-title {
      font-size: 24px;
    }
    
    /* Disabled Event Styles */
    .swiper-slide.isartist.disabled .artist_shows_slide-content > *:not(.event-status-badge) {
      opacity: 0.5;
    }
    
    .swiper-slide.isartist.disabled lvc-favorite-button {
      display: none;
    }
    
    /* Event Status Badge */
    .event-status-badge {
      background-image: url(${config.images.eventStatusLabelMask});
      background-position: 50%;
      background-repeat: no-repeat;
      background-size: cover;
      background-attachment: scroll;
      border-radius: 2px;
      padding: 12px;
      color: var(--primary-color);
      font-family: ${Font.family};
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      position: absolute;
      margin-bottom: 12px;
      z-index: 1;
      top: -8px;
      left: -8px;
    }
    
    .event-status-badge p {
      margin: 0;
      position: relative;
      z-index: 1;
    }
    
    .event-status-badge svg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
    }
    
    /* Responsive Styles */
    @media screen and (max-width: 730px) {
      .event-card:hover {
        transform: scale(1);
      }
      
      .swiper-wrapper.isartist {
        height: 75vw;
      }
      
      .swiper-slide.isartist,
      .swiper-slide.isartist.swiper-slide-active {
        max-width: 90% !important;
        width: 90% !important;
        height: 100%;
        opacity: 1;
      }
      
      .artist_shows_slide-title,
      .swiper-slide.isartist.swiper-slide-active > .artist_shows_slide-content > .event_similar-event-info-content > .artist_shows_slide-title {
        font-size: 20px;
        margin-top: 12px;
      }
      
      .artist_shows_swiper-next-wrapper,
      .artist_shows_swiper-prev-wrapper {
        display: none;
      }
      
      .swiper-scrollbar-horizontal {
        width: 100% !important;
        margin-top: 24px;
      }
    }
    
    @media only screen and (max-width: 563px) {
      .events-title {
        margin-bottom: 1rem;
        font-size: 30px;
      }
    }
  `

  eventListeners: EventListeners = [
    [window, 'lvc:artist-spectacles-events-loaded', this.handleEventData],
    ['#events-container', 'click', this.handleEventClick],
  ]
}
